/**
 * Utility functions for scrolling elements into view.
 */

export interface ScrollOptions {
    /** Scroll behavior - 'smooth' or 'auto' (instant). Default: 'smooth' */
    behavior?: ScrollBehavior;
    /** Vertical alignment - 'center', 'start', 'end', 'nearest'. Default: 'center' */
    block?: ScrollLogicalPosition;
    /** Horizontal alignment. Default: 'nearest' */
    inline?: ScrollLogicalPosition;
    /** Padding (in pixels) to consider element "visible". Default: 50 */
    viewportPadding?: number;
}

/**
 * Check if an element is visible within the viewport.
 * 
 * @param element - The DOM element to check
 * @param padding - Extra padding around viewport bounds (default: 50px)
 * @returns true if the element is fully visible within the viewport
 */
export function isElementInViewport(element: Element, padding = 50): boolean {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
        rect.top >= padding &&
        rect.left >= padding &&
        rect.bottom <= viewportHeight - padding &&
        rect.right <= viewportWidth - padding
    );
}

/**
 * Scroll to an element if it's not already visible in the viewport.
 * Uses smooth scrolling and centers the element in view.
 * 
 * @param element - The DOM element to scroll to
 * @param options - Scroll options
 * @returns true if scroll was triggered, false if element was already visible
 */
export function scrollToElementIfNeeded(
    element: Element | null,
    options: ScrollOptions = {}
): boolean {
    if (!element) return false;

    const {
        behavior = 'smooth',
        block = 'center',
        inline = 'nearest',
        viewportPadding = 50,
    } = options;

    // Check if element is already visible
    if (isElementInViewport(element, viewportPadding)) {
        return false; // Already visible, no scroll needed
    }

    // Scroll the element into view
    element.scrollIntoView({
        behavior,
        block,
        inline,
    });

    return true; // Scroll was triggered
}

/**
 * Find an element matching any of the given selectors and scroll to it if needed.
 * Searches within a container first, then falls back to document.
 * 
 * @param selectors - CSS selector string (can include multiple selectors separated by commas)
 * @param container - Optional container element to search within first
 * @param options - Scroll options
 * @returns The element found (whether scrolled to or already visible), or null if not found
 */
export function findAndScrollToElement(
    selectors: string,
    container?: Element | null,
    options: ScrollOptions = {}
): Element | null {
    let element: Element | null = null;

    // Try within container first
    if (container) {
        element = container.querySelector(selectors);
    }

    // Fallback to document
    if (!element) {
        element = document.querySelector(selectors);
    }

    if (element) {
        scrollToElementIfNeeded(element, options);
    }

    return element;
}
